const UI_HISTORY_KEY = "__crm_ui_history__";

/**
 * Create a browser history entry for UI states such as:
 *
 * Edit popup
 * Filter popup
 * View popup
 * Create popup
 * Details drawer
 * etc.
 *
 * This allows browser/mobile Back button to close the UI first
 * instead of leaving the current page.
 */

export const pushUIState = (state = {}) => {
    if (typeof window === "undefined") return;

    const currentUrl =
        window.location.pathname +
        window.location.search +
        window.location.hash;

    const uiState = {
        ...state,
        __ui: true,
        __url: currentUrl,
        __timestamp: Date.now(),
    };

    window.history.pushState(uiState, "", currentUrl);
};


export const replaceUIState = (state = {}) => {
    if (typeof window === "undefined") return;

    const currentUrl =
        window.location.pathname +
        window.location.search +
        window.location.hash;

    const uiState = {
        ...state,
        __ui: true,
        __url: currentUrl,
        __timestamp: Date.now(),
    };

    window.history.replaceState(uiState, "", currentUrl);
};


/**
 * Initialize global UI history handling.
 *
 * When Back is pressed:
 *
 * 1. If current history entry belongs to a UI state,
 *    dispatch a custom event.
 *
 * 2. The active page can close its modal/filter/drawer.
 *
 * 3. If no UI state exists, normal React Router navigation
 *    continues.
 */
export const initializeUIHistory = () => {

    if (typeof window === "undefined") return;

    // Prevent installing listener multiple times
    if (window.__CRM_UI_HISTORY_INITIALIZED__) {
        return;
    }

    window.__CRM_UI_HISTORY_INITIALIZED__ = true;


    /**
     * Mark the current route as a normal page state.
     */
    const currentState = window.history.state;

    if (
        !currentState ||
        currentState.__crmRoute !== true
    ) {

        window.history.replaceState(
            {
                ...(currentState || {}),
                __crmRoute: true,
            },
            "",
            window.location.href
        );
    }


    /**
     * Browser Back button.
     */
    const handlePopState = (event) => {

        const state = event.state;

        /**
         * UI state exists.
         *
         * Tell the application to close the active UI.
         */
        if (state?.__ui === true) {

            window.dispatchEvent(
                new CustomEvent("crm-ui-back", {
                    detail: state,
                })
            );

            return;
        }

        /**
         * Normal browser navigation.
         *
         * React Router handles this automatically.
         */
    };


    window.addEventListener(
        "popstate",
        handlePopState
    );


    /**
     * Cleanup support.
     */
    window.__CRM_UI_HISTORY_CLEANUP__ = () => {

        window.removeEventListener(
            "popstate",
            handlePopState
        );

        window.__CRM_UI_HISTORY_INITIALIZED__ = false;
    };
};


/**
 * Close current UI state programmatically.
 */
export const closeUIState = () => {

    if (typeof window === "undefined") return;

    window.history.back();
};


/**
 * Check whether the current browser state is a UI state.
 */
export const isUIHistoryState = () => {

    if (typeof window === "undefined") {
        return false;
    }

    return window.history.state?.__ui === true;
};